import { Box } from "@mui/material";

/**
 * Small coloured pill for a ticket status. Accepts a status object with
 * an optional `color` (hex or CSS color). When colour is missing we
 * fall back to a neutral mute treatment so the page still reads cleanly
 * the moment the API returns.
 *
 * The tint is data-driven (each status carries its own colour from the
 * Rails side), so the pill background is a `color-mix` built from that
 * value; the muted fallback resolves from the theme's disabled token so
 * it tracks the palette instead of a hardcoded grey.
 */
export function StatusBadge({
  status,
}: {
  status: { name: string; color?: string | null } | null | undefined;
}): JSX.Element {
  if (!status) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 999,
          bgcolor: "action.hover",
          px: 1,
          py: 0.25,
          fontSize: 12,
          color: "text.disabled",
        }}
      >
        —
      </Box>
    );
  }
  return (
    <Box
      component="span"
      sx={(theme) => {
        const tint = status.color ?? theme.palette.text.disabled;
        return {
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          borderRadius: 999,
          px: 1,
          py: 0.25,
          fontSize: 12,
          fontWeight: 500,
          backgroundColor: `color-mix(in srgb, ${tint} 14%, transparent)`,
          color: tint,
          "& .status-dot": { backgroundColor: tint },
        };
      }}
    >
      <Box
        component="span"
        className="status-dot"
        sx={{ height: 6, width: 6, borderRadius: "50%" }}
      />
      {status.name}
    </Box>
  );
}
